import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    const msg = Array.isArray(detail)
      ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' ')
      : typeof detail === 'string'
        ? detail
        : `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export function AuthProvider({ children }) {
  // `null`  = checking, object = signed in, `false` = not signed in
  const [admin, setAdmin] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await jsonFetch(`${API}/api/auth/me`);
      setAdmin(data.admin);
    } catch {
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await jsonFetch(`${API}/api/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(async () => {
    try {
      await jsonFetch(`${API}/api/auth/logout`, { method: 'POST' });
    } finally {
      setAdmin(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ admin, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export { jsonFetch };
